/**
 * Landing v2 — "Möt en värd" carousel.
 * Horizontal swipe on mobile, 2–3 cards on desktop.
 * Reuses media_assets SoT via useFeaturedHosts.
 */
import { Link } from 'react-router-dom';
import { ArrowRight, MapPin } from 'lucide-react';
import { useFeaturedHosts } from '@/hooks/useFeaturedHosts';
import { Skeleton } from '@/components/ui/skeleton';
import { useTranslate } from '@/i18n';

export default function HostCarouselSection() {
  const { data: hosts = [], isLoading } = useFeaturedHosts();
  const t = useTranslate();

  if (isLoading) return <CarouselSkeleton />;
  if (hosts.length === 0) return null;

  return (
    <section className="bg-farmly-soft py-16 sm:py-24">
      <div className="container max-w-6xl px-5">
        {/* Eyebrow */}
        <p className="text-xs tracking-widest uppercase text-muted-foreground/60 mb-2">
          {t("home.hosts.kicker")}
        </p>
        <h2 className="font-display text-2xl sm:text-3xl font-semibold text-foreground tracking-[-0.015em] leading-[1.15] mb-8 sm:mb-10">
          {t("home.hosts.title")}
        </h2>

        {/* Scroll container */}
        <div className="flex gap-5 overflow-x-auto snap-x snap-mandatory scroll-smooth pb-4 -mx-5 px-5 scrollbar-none">
          {hosts.map((host) => (
            <HostCard key={host.id} host={host} />
          ))}
        </div>
      </div>
    </section>
  );
}

function HostCard({ host }: { host: ReturnType<typeof useFeaturedHosts>['data'] extends (infer T)[] ? T : never }) {
  const location = [host.city, host.region].filter(Boolean).join(', ');
  const t = useTranslate();

  return (
    <Link
      to={host.href}
      className="group flex-shrink-0 w-[80vw] sm:w-[45%] lg:w-[31%] snap-start rounded-xl overflow-hidden bg-background shadow-sm hover:shadow-md transition-shadow duration-300"
    >
      {/* Image */}
      <div className="aspect-[4/3] overflow-hidden bg-muted">
        {host.coverUrl ? (
          <img
            src={host.coverUrl}
            alt={host.name}
            className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.03]"
            loading="lazy"
          />
        ) : (
          <div className="h-full w-full bg-gradient-to-br from-muted to-muted/60" />
        )}
      </div>

      {/* Text */}
      <div className="p-4 sm:p-5 flex flex-col gap-2">
        <h3 className="font-display text-lg font-semibold text-foreground leading-tight">
          {host.name}
        </h3>

        {location && (
          <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
            <MapPin className="h-3.5 w-3.5 shrink-0" />
            <span>{location}</span>
          </div>
        )}

        {host.description && (
          <p className="text-sm text-muted-foreground leading-relaxed line-clamp-2">
            {host.description}
          </p>
        )}

        <span className="mt-1 inline-flex items-center gap-1.5 text-sm font-medium text-accent group-hover:text-accent/80 transition-colors w-fit">
          {t("home.hosts.cardCta")}
          <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5" />
        </span>
      </div>
    </Link>
  );
}

function CarouselSkeleton() {
  return (
    <section className="bg-farmly-soft py-16 sm:py-24">
      <div className="container max-w-6xl px-5">
        <Skeleton className="h-3 w-20 mb-2" />
        <Skeleton className="h-8 w-64 mb-8" />
        <div className="flex gap-5 overflow-hidden">
          {[0, 1, 2].map((i) => (
            <div key={i} className="flex-shrink-0 w-[80vw] sm:w-[45%] lg:w-[31%] space-y-3">
              <Skeleton className="aspect-[4/3] rounded-xl w-full" />
              <Skeleton className="h-5 w-3/4" />
              <Skeleton className="h-4 w-1/3" />
              <Skeleton className="h-8 w-full" />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
